/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Login from "./Login";
import ReferralPopup from "./ReferralPopup";
import HowToPlayPopup from "./HowToPlayPopup";
import AudioToggleButton from "./AudioToggleButton";
import { useAdmin } from "../hooks/useAdmin";
import styles from "../styles/TopNavigation.module.css";

interface TopNavigationProps {
  fixed?: boolean;
}

const TopNavigation: React.FC<TopNavigationProps> = ({ fixed }: TopNavigationProps) => {
  const location = useLocation();
  const { isAdmin } = useAdmin();
  const [showReferralPopup, setShowReferralPopup] = useState(false);
  const [showHowToPlayPopup, setShowHowToPlayPopup] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen && navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Don't show navigation on admin pages
  if (location.pathname === "/admin") {
    return null;
  }

  return (
    <nav className={`${styles.topNavigation} ${fixed ? styles.fixed : ""}`} ref={navRef}>
      <div className={styles.navBrand}>
        <h1>
          <a href="/" className={styles.navLink}>
            <img src="/icon.png" alt="DeathX Fun" className={styles.navIcon} /> DEATHX FUN
          </a>
        </h1>
      </div>

      {/* Mobile FAQ Button */}
      <button
        className={styles.mobileFaqButton}
        onClick={(e) => {
          setShowHowToPlayPopup(true);
          e.preventDefault();
        }}
        aria-label="Open FAQ"
      >
        FAQ
      </button>

      {/* Mobile Audio Toggle Button */}
      <AudioToggleButton className={styles.mobileAudioToggle} />

      <button
        className={styles.mobileMenuToggle}
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileMenuOpen}
      >
        <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.active : ""}`}></span>
        <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.active : ""}`}></span>
        <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.active : ""}`}></span>
      </button>

      <div className={`${styles.navLinks} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}>
        <a
          href="#"
          className={`${styles.navLink} desktop-only`}
          onClick={(e) => {
            setShowHowToPlayPopup(true);
            closeMobileMenu();
            e.preventDefault();
          }}
        >
          FAQ
        </a>
        <a
          href="https://deathx-fun.notion.site/DeathX-fun-Docs-24e1b048608b80f69c33d28127a39c53"
          className={styles.navLink}
          target="_blank"
          rel="noreferrer"
          onClick={closeMobileMenu}
        >
          DOCS
        </a>
        <a href="/verifier" className={styles.navLink} onClick={closeMobileMenu}>
          VERIFIER
        </a>
        <a
          href="#"
          className={styles.navLink}
          onClick={(e) => {
            setShowReferralPopup(true);
            closeMobileMenu();
            e.preventDefault();
          }}
        >
          REFERRAL
        </a>
        <a
          href="https://taskon.xyz/DeathXFun"
          className={styles.navLink}
          target="_blank"
          rel="noreferrer"
          onClick={closeMobileMenu}
        >
          QUESTS
        </a>
        {isAdmin && (
          <a href="/admin" className={styles.navLink} onClick={closeMobileMenu} title="Admin Panel">
            🛠️ ADMIN
          </a>
        )}
        <AudioToggleButton className={styles.desktopAudioToggle} />
        <div className={styles.navAuth}>
          <Login onWalletAddressClick={closeMobileMenu} />
        </div>
      </div>

      <ReferralPopup isVisible={showReferralPopup} onClose={() => setShowReferralPopup(false)} />
      <HowToPlayPopup isVisible={showHowToPlayPopup} onClose={() => setShowHowToPlayPopup(false)} />
    </nav>
  );
};

export default TopNavigation;
